import { Fragment } from 'react'
import styled from 'styled-components'
import { Wrapper } from '@farewill/ui'
import { COLOR, FONT, GTR } from '@farewill/ui/tokens'

import { FuneralPlanVersion } from 'lib/models/funeral-plan'

import { formatPaymentDate, formatPence } from './payment-shared'

const StyledDetails = styled(Wrapper)`
  display: flex;
  flex-direction: column;
  gap: ${GTR.S};
  color: ${COLOR.BLACK};
  font-size: ${FONT.SIZE.S};
`

const StyledHeading = styled.div`
  font-size: ${FONT.SIZE.M};
  font-weight: ${FONT.WEIGHT.BOLD};
`

const StyledRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${GTR.S};
`

/** A short rule under the amounts, between the lines and the totals */
const StyledRule = styled.hr`
  align-self: flex-end;
  width: calc(${GTR.XL} * 3);
  margin: 0;
  border: 0;
  border-top: 1px solid ${COLOR.BLACK};
`

type Row = [label: string, value: string]

/**
 * The rows for an agreement, as the design sets them out: any balance
 * carried in and each of the agreement's own lines, then what it comes to,
 * and for instalments the term, monthly payments and payment date.
 */
const groupsOf = ({
  type,
  agreement,
  balanceIn,
  timelineQuote,
  payments,
  stillToPayInPence,
}: FuneralPlanVersion): Row[][] => {
  const changes = Boolean(agreement.supersedesAgreementId)

  const totalLabel =
    stillToPayInPence < 0
      ? 'Total to refund'
      : changes
        ? 'New total to pay'
        : 'Total to pay'

  const carriedIn: Row[] = balanceIn
    ? [
        [
          `Balance from Agreement ${balanceIn.fromAgreementNumber}`,
          formatPence(balanceIn.amountInPence),
        ],
      ]
    : []
  const instalments: Row[] =
    type === 'Instalments'
      ? [
          [
            changes ? 'Remaining term' : 'Term',
            `${payments.count} ${payments.count === 1 ? 'month' : 'months'}`,
          ],
          [
            'Monthly payments',
            `${formatPence(payments.amountInPence)} a month`,
          ],
          ['Payment date', formatPaymentDate(payments)],
        ]
      : []

  return [
    [
      ...carriedIn,
      ...timelineQuote.map(({ description, amountInPence }): Row => [
        description,
        formatPence(amountInPence),
      ]),
    ],
    [[totalLabel, formatPence(Math.abs(stillToPayInPence))], ...instalments],
  ]
}

/**
 * What an agreement agreed, behind "See details" in the payment timeline
 * (2.14): its lines, then what they come to and how it is paid.
 */
const PaymentAgreementDetails = ({
  version,
}: {
  version: FuneralPlanVersion
}): React.ReactElement => (
  <StyledDetails data-testid="agreement-details">
    <StyledHeading>Agreement {version.number}</StyledHeading>
    {groupsOf(version)
      .filter((group) => group.length > 0)
      .map((group, index) => (
        <Fragment key={index}>
          {index > 0 && <StyledRule />}
          {group.map(([label, value], rowIndex) => (
            <StyledRow key={rowIndex} data-testid="detail-row">
              <span>{label}</span>
              <span>{value}</span>
            </StyledRow>
          ))}
        </Fragment>
      ))}
  </StyledDetails>
)

export default PaymentAgreementDetails
